import { BasePresenter } from "core/presenter";
import { Sellable } from "core/entity";
import { fn } from "util";
import {
  ProductRepository,
  BarcodeRepository,
  OptionalGroupRepository,
  VariationRepository
} from "core/repository";
import validate from "util/validate";
import { ProductConstraint, BarcodeConstraint } from "core/constraint";
import Big from "big.js";
import _ from "lodash";

export default class ProductFormPresenter extends BasePresenter {
  initialState() {
    return {
      title: "Novo Produto",
      mode: "add",
      loading: false,
      form: new Sellable(),
      errors: {
        name: "",
        code: "",
        cost_value: "",
        sell_value: "",
        taxes: {
          cfop: "",
          ncm: "",
          cest: "",
          csosn: ""
        },
        barcodeErrors: []
      },
      defaultTaxes: {},
      canEditStockCount: false,
      optionalGroupsOpen: false,
      optionalGroupsUuids: [],
      linkedOptionalGroups: [],
      companyFiscalInformations: {},
      selectedVariations: [],
      variationsListOptions: [],
      fullVariationsListOptions: [],
      variationsList: [],
      skuLoading: false,
      skuPagination: {
        perPage: 25,
        currentPage: 1,
        total: 0
      },
      skuOrder: {
        field: "name",
        direction: "ASC"
      },
      hasSku: false,
      skuResultSet: [],
      skuResultSetErrors: []
    };
  }

  onGoBack() {
    this.navigateTo("app.registration.product", {});
  }

  async onStart() {
    const currentUser = await this.mRuntime.get("currentUser");

    if (!currentUser.company.features.includes("manage_sellable")) {
      return this.navigateTo("app.registration.product");
    }

    this.viewModel.loading = true;
    await this.loadProduct();
    this.viewModel.loading = false;
  }

  async loadProduct() {
    const currentUser = await this.mRuntime.get("currentUser");
    const result = await this.getSellable();
    const barcodes = await this.loadBarcodes();

    if (result.success) {
      this.viewModel.companyFiscalInformations =
        currentUser.company.fiscal_informations;
      this.viewModel.defaultTaxes = currentUser.company.default_taxes;
      this.viewModel.form = result.data;
      this.viewModel.form.barcodes = barcodes;
      this.viewModel.optionalGroupsUuids = result.data.optional_groups_uuids;
      // this.loadSkus();
    } else {
      this.sendNotification({
        type: "error",
        title: "Oooops...",
        content:
          "Não conseguimos carregar este produto! Tente novamente em alguns minutos."
      });
    }
  }

  async getSellable() {
    const sellable = new Sellable();
    sellable.taxes.use_default_configuration = true;
    await this.loadOptionalGroups(sellable);
    await this.loadProductVariations(sellable);
    return { success: true, data: sellable };
  }

  async loadOptionalGroups(product) {
    const optionalGroupsUuids = product.optional_groups_uuids || [];
    const optionalGroups = await this.getOptionalGroups();
    const linkedOptionalGroups = [];

    optionalGroupsUuids.forEach(opt => {
      const linkedOptionalGroup = optionalGroups.filter(optGroup => {
        return optGroup.uuid === opt;
      });

      if (linkedOptionalGroup.length) {
        linkedOptionalGroups.push(linkedOptionalGroup[0]);
      }
    });

    this.viewModel.linkedOptionalGroups = linkedOptionalGroups;
  }

  async loadBarcodes() {
    this.viewModel.loading = true;
    const result = await BarcodeRepository.getListOnline({
      order: [
        {
          field: "code",
          direction: "ASC"
        }
      ],
      pagination: {
        page: 1,
        perPage: 25
      },
      filters: [
        {
          field: "sellable_uuid",
          value: this.routeParams.productUuid,
          operation: "EQ"
        }
      ]
    });

    if (result.success) {
      return result.data;
    } else {
      this.sendNotification({
        type: "error",
        title: "Oops",
        content: "Não foi possível carregar os códigos de barras desse produto."
      });
    }
    this.viewModel.loading = false;
  }

  async getOptionalGroupsFilters() {
    const currentUser = await this.mRuntime.get("currentUser");
    const companyUuid = currentUser.company_uuid;
    return [
      // { connector: 'AND', field: 'enabled', operation: 'EQ', value: 'true' }, commented so it show disabled optGroups
      {
        connector: "AND",
        field: "company_uuid",
        operation: "EQ",
        value: companyUuid
      },
      { connector: "AND", field: "deleted", operation: "EQ", value: "false" }
    ];
  }

  async getOptionalGroups() {
    this.viewModel.loading = true;

    const filters = await this.getOptionalGroupsFilters();
    const pagination = { page: 1 /* , perPage: 25 */ };
    const order = [{ field: "name", direction: "ASC" }];

    const repositoryResult = await OptionalGroupRepository.getListOnline({
      order,
      pagination,
      filters
    });

    if (repositoryResult.success) {
      this.viewModel.loading = false;
      return repositoryResult.data;
    } else if (repositoryResult.fatal) {
      this.sendNotification({
        type: "error",
        title: "Oops...",
        content:
          "Nosso serviço encontra-se indisponível. Tente novamente mais tarde."
      });
    } else {
      this.sendNotification({
        type: "error",
        title: "Oops...",
        content:
          "Não foi possível carregar os grupos de opcionais. Tente novamente mais tarde."
      });
    }

    this.viewModel.loading = false;
  }

  onDefaultConfigurationChange(checked) {
    this.viewModel.form.taxes.use_default_configuration = checked;

    if (checked === false) {
      this.verifyCrt();
    }
  }

  async onSendForm() {
    this.viewModel.loading = true;

    const isValidKit = this.doValidateKit();

    if (!isValidKit) {
      this.viewModel.loading = false;
      return;
    }

    const isValid = await this.doValidateForm();
    const isValidSku = this.doValidateSku();

    if (isValid && isValidSku) {
      return this.persist();
    }

    let message = "";
    if (!isValid && !isValidSku) {
      message = "nos dados do produto e nos dados dos SKUs";
    } else if (!isValid && isValidSku) {
      message = "nos dados do produto";
    } else if (!isValidSku && isValid) {
      message = "nos dados dos SKUs";
    }

    this.sendNotification({
      type: "warning",
      content: `Existem campos inválidos ${message}. Verifique os dados preenchidos.`
    });

    this.viewModel.loading = false;
  }

  async persist() {
    const sellable = this.viewModel.form;
    const taxes = [
      "cest",
      "cfop",
      "csosn",
      "ncm",
      "pis",
      "cofins",
      "origin",
      "icms",
      "pis_normal",
      "cofins_normal"
    ];
    const crt3fields = {
      icms: [
        "cst",
        "cfop",
        "modBC",
        "pICMS",
        "pFCP",
        "modBCST",
        "pMVAST",
        "pRedBCST",
        "pICMSST",
        "pFCPST",
        "pRedBC",
        "motDesICMS",
        "pST",
        "pFCPSTRet",
        "pRedBCEfet",
        "pICMSEfet"
      ],
      pis_normal: ["cst", "pPIS", "vAliqProd"],
      cofins_normal: ["cst", "pCOFINS", "vAliqProd"]
    };
    taxes.forEach(tax => {
      if (fn.isBlank(sellable.taxes[tax]) || sellable.taxes[tax] === "none") {
        delete sellable.taxes[tax];
      } else {
        if (["icms", "pis_normal", "cofins_normal"].includes(tax)) {
          crt3fields[tax].forEach(field => {
            if (
              fn.isBlank(sellable.taxes[tax][field]) ||
              sellable.taxes[tax][field] === "none"
            ) {
              delete sellable.taxes[tax][field];
            }
          });
        }
      }
    });

    if (sellable.taxes.use_default_configuration) {
      const defaultTaxes = [
        "cfop",
        "csosn",
        "pis",
        "cofins",
        "origin",
        "icms",
        "pis_normal",
        "cofins_normal"
      ];
      defaultTaxes.forEach(tax => {
        delete sellable.taxes[tax];
      });
    }

    const optionalGroupsUuids = this.viewModel.optionalGroupsUuids;
    sellable.optional_groups_uuids = optionalGroupsUuids;

    if (sellable.type === "product_kit") {
      const stock = ["stock_count", "stock_value"];
      stock.forEach(attribute => delete sellable[attribute]);
      sellable.optional_groups_uuids = [];
    }

    const { selectedVariations } = this.viewModel;
    const selectedVariationsUuids = selectedVariations.reduce(
      (uuidAccumulator, variation) => {
        if (variation.uuid) {
          uuidAccumulator.push(variation.uuid);
        }
        return uuidAccumulator;
      },
      []
    );
    sellable.variations_uuids = selectedVariationsUuids;
    sellable.stock_value = 0;

    this.proceedPersistence(sellable);
  }

  async proceedPersistence(sellable) {
    const response = await ProductRepository.create(sellable);
    this.postPersist(response, sellable);
  }

  async postPersist(response, sellable, deleteSkus) {
    const { mode } = this.viewModel;

    if (!response.success) {
      const expression = mode === "add" ? "adicionar" : "atualizar";
      const defaultMessage = `Ocorreu um erro ao ${expression} este produto. Tente novamente mais tarde.`;

      this.sendNotification({
        type: "error",
        title: "Oops...",
        content: response.error || defaultMessage
      });

      this.viewModel.loading = false;
      return;
    }

    if (sellable.barcodes.length > 0) {
      for (let barcode of sellable.barcodes) {
        if (!barcode.mode) {
          continue;
        }

        if (barcode.mode === "edit") {
          await this.updateBarcode(barcode.uuid, barcode.code);
        } else if (barcode.mode === "delete") {
          await this.deleteBarcode(barcode.uuid);
        } else {
          await this.addBarcode(barcode);
        }
      }
    }

    /*
    if (sellable.variations_uuids.length && !this.viewModel.skuResultSet.length) {
      let skusCreated = false;
      let deletedSkus = false;

      if (mode === 'edit') {
        if (deleteSkus) {
          const skusDeleted = await ProductRepository.deleteSkus(sellable.uuid);
          const newSkus = await ProductRepository.generateSkus(sellable.uuid);
          if (skusDeleted.success) {
            deletedSkus = true;
          }
          if (newSkus.success) {
            skusCreated = true;
          }
        }

      }

      this.sendNotification({
        type: 'confirmation',
        title: 'Sucesso!',
        content: `O produto foi ${mode === 'add' ? 'adicionado' : 'atualizado'}.
          Percebemos que você adicionou variações ao produto. ${deletedSkus ? 'Os SKUs antigos foram excluídos.' : ''}
          ${skusCreated ? 'A nova grade de SKUs foi gerada corretamente.' : 'Não foi possível gerar a nova grade de SKUs.'}
          ${mode === 'add' ?
            'Deseja ser redirecionado para a tela de edição de produtos para alterar os SKUs?' :
            'Deseja continuar na tela de edição de produto para alterar os SKUs?'}`,
        actions: [
          {
            label: 'Não',
            callback: () => this.redirectToDataTable(mode)
          },
          {
            label: `${mode === 'add' ?
            'Sim, quero ser redirecionado.' :
            'Sim, quero continuar na tela de edição.'}`,
            type: 'accent',
            callback: () => this.redirectToProductEdit(sellable.uuid, mode)
          },
        ]
      });
    }

    if (this.viewModel.skuResultSet.length) {
      const skuMessage = await this.updateSkus();
      const parsedSkuMessages = skuMessage.join('\n')
      // Using error but it's not an error
      this.sendNotification({
        type: 'error',
        title: 'SKUs atualizados!',
        content: `O produto foi ${mode === 'add' ? 'adicionado' : 'atualizado'}. \n${parsedSkuMessages}`
      });
    } else {
    */
    this.sendNotification({
      type: "success",
      content: `O produto foi ${mode === "add" ? "adicionado" : "atualizado"}.`
    });
    this.navigateTo("app.registration.product");
    // }

    this.viewModel.loading = false;
  }

  redirectToDataTable(mode) {
    this.navigateTo("app.registration.product");
    setTimeout(
      () =>
        this.sendNotification({
          type: "success",
          content: `O produto foi ${
            mode === "add" ? "adicionado" : "atualizado"
          }.`
        }),
      300
    );
  }

  redirectToProductEdit(productUuid, mode) {
    this.navigateTo("app.registration.product.edit", { productUuid });
    this.loadSkus();
    setTimeout(
      () =>
        this.sendNotification({
          type: "success",
          content: `O produto foi ${
            mode === "add" ? "adicionado" : "atualizado"
          }.`
        }),
      300
    );
  }

  async addBarcode(barcode) {
    const sellable = this.viewModel.form;

    const result = await BarcodeRepository.create({
      code: barcode.code,
      type: barcode.type,
      company_uuid: sellable.company_uuid,
      account_uuid: sellable.company_uuid,
      sellable_uuid: sellable.uuid,
      uuid: barcode.uuid
    });

    if (result.success) {
      this.sendNotification({
        type: "success",
        content: "O código de barras foi adicionado."
      });
    } else {
      this.sendNotification({
        type: "error",
        title: "Oooops...",
        content: result.error
      });
    }
  }

  async updateBarcode(barcodeUuid, newCode) {
    const sellable = this.viewModel.form;

    const updateBarcode = await BarcodeRepository.update(
      sellable.company_uuid,
      barcodeUuid,
      { code: newCode }
    );

    if (updateBarcode.success) {
      this.sendNotification({
        type: "success",
        content: "O código de barras foi atualizado."
      });
    } else {
      this.sendNotification({
        type: "error",
        title: "Oooops...",
        content: updateBarcode.error
      });
    }
  }

  async deleteBarcode(barcodeUuid) {
    const sellable = this.viewModel.form;

    const findResult = await BarcodeRepository.getListOnline({
      order: [
        {
          field: "uuid",
          direction: "ASC"
        }
      ],
      pagination: {
        page: 1,
        perPage: 25
      },
      filters: [{ field: "uuid", value: barcodeUuid, operation: "EQ" }]
    });

    if (findResult.success && !findResult.data.length) {
      return;
    }

    const result = await BarcodeRepository.delete(
      sellable.company_uuid,
      barcodeUuid
    );

    if (result.success) {
      this.sendNotification({
        type: "success",
        content: "O código de barras foi excluído."
      });
    } else {
      this.sendNotification({
        type: "error",
        title: "Oooops...",
        content: result.error
      });
    }
  }

  async doValidateForm() {
    const constraint = ProductConstraint.add;
    const prodParams = this.viewModel.form;
    const toValidateBarcodes = prodParams.barcodes.filter(
      bc => bc.mode !== "delete"
    );
    const barcodesCode = _.map(toValidateBarcodes, bc => ({
      code: bc.code,
      type: bc.type
    })) || [{}];
    const { taxes } = prodParams;
    const currentUser = await this.mRuntime.get("currentUser");

    const taxesToValidate = {
      state: currentUser.company.address.state || "SP",
      code: taxes.ncm
    };

    const barcodeErrors = this.doValidateBarcodes(barcodesCode);
    const emptyBarcodeErrors =
      barcodeErrors.length === 0 || barcodeErrors.every(e => e === "");

    let validation = validate(prodParams, constraint, { fullMessages: false });

    if (this.viewModel.form.taxes.ncm && this.viewModel.form.taxes.ncm.length) {
      const validatedTaxes = await ProductRepository.validateTaxes(
        taxesToValidate
      );

      if (validatedTaxes.status !== 403) {
        if (!validatedTaxes.success) {
          this.sendNotification({
            type: "warning",
            content:
              "Não foi possível validar o cadastro do NCM. Deixe-o em branco para salvar."
          });
        }

        if (validatedTaxes.data && !validatedTaxes.data.found) {
          if (!validation) {
            validation = {};
          }
          validation["taxes.ncm"] = ["NCM inválido"];
        }
      }
    }

    if (!!!emptyBarcodeErrors) {
      this.viewModel.errors["barcodeErrors"] = barcodeErrors;
      return false;
    }

    if (!!validation) {
      const errors = Object.entries(validation).map(v => {
        const field = v[0];
        const validationErrors = v[1];

        return {
          field,
          content: validationErrors.join(", ")
        };
      });

      this.viewModel.errors = [];
      errors.forEach(
        error => (this.viewModel.errors[error.field] = error.content)
      );

      return false;
    }

    return true;
  }

  doValidateKit() {
    const { form } = this.viewModel;

    if (form.type === "product" || form.type === "sku") {
      return true;
    }

    const quantities = Object.values(form.products_uuids);

    const hasInvalidQuantities = quantities.find(quantity => !quantity);

    if (hasInvalidQuantities) {
      this.sendNotification({
        type: "warning",
        content: "Verifique a quantidade de cada produto no Combo/Kit."
      });

      return false;
    }

    // Kit has two itens or more OR the only item has quantity > 1
    const isValidKit = quantities.length > 1 || quantities[0] > 1;

    if (!isValidKit) {
      this.sendNotification({
        type: "warning",
        content: "Escolha pelo menos 2 produtos para compor o Kit."
      });

      return false;
    }

    return true;
  }

  doValidateBarcodes(barcodes) {
    let barcodeErrors = [];
    let codeName = "Código de barras";
    const constraint = BarcodeConstraint.barCode;

    barcodes.forEach(barcode => {
      if (barcode.type === "gtin") {
        codeName = "GTIN";
      } else if (barcode.type === "pack_barcode") {
        codeName = "GTIN/Embalagem";
      } else {
        codeName = "Código de barras";
      }

      const barcodeToBeValidated = {
        code: barcode.code,
        type: barcode.type,
        codeName
      };
      const validation = validate(barcodeToBeValidated, constraint, {
        fullMessages: false
      });
      if (!!validation) {
        barcodeErrors.push(validation.code[0]);
      } else {
        barcodeErrors.push("");
      }
    });

    return barcodeErrors;
  }

  async onOptionalGroupsOpen() {
    this.viewModel.optionalGroupsOpen = true;
  }

  async onOptionalGroupsClose() {
    const { linkedOptionalGroups } = this.viewModel;
    const optionalGroupsUuids = [];

    linkedOptionalGroups.forEach(optionalGroup =>
      optionalGroupsUuids.push(optionalGroup.uuid)
    );

    this.viewModel.optionalGroupsOpen = false;
    this.viewModel.optionalGroupsUuids = optionalGroupsUuids;
  }

  async onOptionalGroupsSave(updatedOptionalGroups) {
    const product = this.viewModel.form;
    product.optional_groups_uuids = updatedOptionalGroups;
    await this.loadOptionalGroups(product);
    await this.onOptionalGroupsClose();
  }

  async loadProductVariations(sellable) {
    this.viewModel.loading = true;

    const order = [{ field: "name", direction: "ASC" }];
    const pagination = { page: 1 };

    const filters = [
      { connector: "AND", field: "deleted", operation: "NOT", value: true }
    ];
    const repositoryResult = await VariationRepository.getListOnline({
      order,
      pagination,
      filters
    });

    if (repositoryResult.success) {
      const variationsList = _.map(repositoryResult.data, variation => {
        return {
          name: variation.name,
          uuid: variation.uuid,
          variation_options: variation.variation_options
        };
      });
      const fullVariationsListOptions = _.map(variationsList, variation => {
        return { value: variation.uuid, label: variation.name };
      });

      fullVariationsListOptions.unshift({ value: "", label: "Selecione" });

      this.viewModel.variationsList = variationsList;
      this.viewModel.fullVariationsListOptions = fullVariationsListOptions;
      this.viewModel.variationsListOptions = fullVariationsListOptions;

      if (sellable && sellable.variations_uuids) {
        this.viewModel.canEditStockCount = false;
        this.loadSelectedVariations(variationsList, sellable);
      } else {
        this.viewModel.canEditStockCount = true;
      }
    } else if (!repositoryResult.userCancelled) {
      this.sendNotification({
        type: "error",
        title: "Ooops...",
        content:
          "Houve um problema ao buscar as variações.\nTente novamente mais tarde ou entre em contato com o suporte."
      });
    }

    this.viewModel.loading = false;
  }

  loadSelectedVariations(variationsList, sellable) {
    const variationsUuid = sellable.variations_uuids || [];
    const selectedVariations = [];

    variationsList.forEach(variation => {
      if (variationsUuid.includes(variation.uuid)) {
        this.viewModel.canEditStockCount = false;
        const foundVariation = {
          name: variation.name,
          uuid: variation.uuid,
          variation_options: variation.variation_options
        };
        const foundVariationOption = {
          value: variation.uuid,
          label: variation.name
        };
        selectedVariations.push(foundVariation);
        this.updateVariationsListOptions(foundVariationOption, "found");
      }
    });

    this.viewModel.selectedVariations = selectedVariations;
  }

  newVariationsOption() {
    this.viewModel.selectedVariations.push({
      name: "",
      uuid: "",
      variation_options: []
    });
  }

  async selectVariationsListOption(index, variationUuid) {
    this.viewModel.loading = true;

    const result = await VariationRepository.getVariation(variationUuid);

    if (result.success) {
      const { data } = result;
      const selectedVariation = {
        name: data.name,
        uuid: data.uuid,
        variation_options: data.variation_options
      };
      const selectedVariationOption = { value: data.uuid, label: data.name };

      this.viewModel.selectedVariations[index] = selectedVariation;
      this.updateVariationsListOptions(selectedVariationOption, "selected");
    } else {
      this.sendNotification({
        type: "error",
        title: "Oooops...",
        content: "Não foi esta variação! Tente novamente em alguns minutos."
      });
    }

    this.viewModel.loading = false;
  }

  async deleteVariationsOption(index, variation) {
    const selectedVariations = _.clone(
      this.viewModel.selectedVariations.slice()
    );
    selectedVariations.splice(index, 1);
    this.viewModel.selectedVariations = selectedVariations;
    this.updateVariationsListOptions(variation, "deleted");
    if (!selectedVariations.length) {
      this.viewModel.canEditStockCount = true;
    }
  }

  updateVariationsListOptions(variation, option) {
    if (variation.value) {
      if (option === "deleted") {
        this.viewModel.variationsListOptions.push(variation);
      } else {
        const variationsListOptions = _.clone(
          this.viewModel.variationsListOptions.slice()
        );
        const index = variationsListOptions.findIndex(
          item => item.value === variation.value
        );
        variationsListOptions.splice(index, 1);
        this.viewModel.variationsListOptions = variationsListOptions;
      }
    }
  }

  setLoading(value) {
    this.viewModel.loading = value;
  }

  async loadSkus() {
    const repositoryResult = await ProductRepository.getListOnline({
      order: [this.viewModel.skuOrder],
      pagination: {
        page: this.viewModel.skuPagination.currentPage,
        perPage: this.viewModel.skuPagination.perPage
      },
      filters: await this.getSkuFilters()
    });

    if (repositoryResult.success) {
      // Required to load barcode of each product
      await this.loadEachSkuData(repositoryResult);
    } else if (!repositoryResult.userCancelled) {
      this.sendNotification({
        type: "error",
        title: "Ocorreu um erro",
        content:
          "Não foi possível carregar os dados. Tente novamente mais tarde."
      });
    }
  }

  async getSkuFilters() {
    const productUuid = await this.router.stateService.params.productUuid;

    const filters = [
      { connector: "AND", field: "type", operation: "EQ", value: "sku" },
      {
        connector: "AND",
        field: "sku_uuid",
        operation: "EQ",
        value: productUuid
      }
    ];

    return filters;
  }

  async loadEachSkuData(repositoryResultSkus) {
    this.viewModel.skuLoading = true;

    const repoData = repositoryResultSkus.data;

    if (repoData.length) {
      this.viewModel.hasSku = true;
      const stockCountResponse = await ProductRepository.getSkuStock(
        this.viewModel.form.uuid
      );
      if (stockCountResponse.success) {
        this.viewModel.form.stock_count = stockCountResponse.data.stock_count;
      }
    }

    const { skuResultSet } = this.viewModel;
    const { skuResultSetErrors } = this.viewModel;

    for (const [index, sku] of repoData.entries()) {
      const barcodesRepo = await this.getBarcodes(sku.uuid);

      const result = {
        uuid: sku.uuid,
        code: sku.code,
        barcodes: [],
        name: sku.name,
        stock_count: sku.stock_count || "0.0",
        sell_value: sku.sell_value,
        deleted: sku.deleted,
        company_uuid: sku.company_uuid,
        resultSetIndex: index,
        edited: false
      };

      if (barcodesRepo.success) {
        result.barcodes = barcodesRepo.data.slice();
      }

      skuResultSet.push(result);
      skuResultSetErrors.push({});
    }
    const skuPagination = this.viewModel.skuPagination;
    skuPagination.total = repositoryResultSkus.total;
    skuPagination.perPage = repositoryResultSkus.perPage;

    this.viewModel.skuResultSet = skuResultSet;
    this.viewModel.skuResultSetErrors = skuResultSetErrors.slice();
    this.viewModel.skuPagination = skuPagination;
    this.viewModel.skuLoading = false;
  }

  async getBarcodes(productUuid) {
    return await BarcodeRepository.getListOnline({
      order: [{ field: "code", direction: "ASC" }],
      pagination: { page: 1 },
      filters: [{ field: "sellable_uuid", value: productUuid, operation: "EQ" }]
    });
  }

  refreshResultSetBarcodes(barcodes, index) {
    this.viewModel.skuResultSet[index].barcodes = barcodes;
    // Gotta check if its viable when a barcode is added and removed after (too much to control?)
    this.viewModel.skuResultSet[index].edited = true;
  }

  onChangeResultSetField(index, field, value) {
    this.viewModel.skuResultSet[index][field] = value;
    this.viewModel.skuResultSet[index].edited = true;

    // Just to make sure viewModel is updated (idk, basepresenter bugs?)
    if (field !== "sell_value") {
      this.viewModel.skuLoading = true;
      this.viewModel.skuLoading = false;
    }
  }

  onSaveModal(barcodes, index) {
    this.viewModel.skuResultSet[index].barcodes = barcodes;
  }

  doValidateSku() {
    const allSkus = this.viewModel.skuResultSet;
    const editedSkus = allSkus.filter(sku => sku.edited);
    const hasError = [];

    if (!editedSkus.length) {
      return true;
    } else {
      const constraint = ProductConstraint.sku;
      editedSkus.forEach(sku => {
        this.viewModel.skuResultSetErrors[sku.resultSetIndex] = {};
        let validation = validate(sku, constraint, { fullMessages: false });

        if (!!validation) {
          const errors = Object.entries(validation).map(v => {
            const field = v[0];
            const validationErrors = v[1];

            return {
              field,
              content: validationErrors.join(", ")
            };
          });

          const parsedError = {};
          this.viewModel.skuResultSetErrors[sku.resultSetIndex] = {};

          errors.forEach(error => (parsedError[error.field] = error.content));

          this.viewModel.skuResultSetErrors[sku.resultSetIndex] = parsedError;

          hasError.push(`error-${sku.resultSetIndex + 1}`);
        }
        return true;
      });

      const noErrors = hasError.length ? false : true;
      return noErrors;
    }
  }

  async updateSkus() {
    this.viewModel.skuLoading = true;
    const { form } = this.viewModel;
    const allSkus = this.viewModel.skuResultSet;
    const editedSkus = allSkus.filter(sku => sku.edited);
    const finalMessage = [];

    if (editedSkus.length) {
      for (const sku of editedSkus) {
        sku.sell_value = sku.sell_value
          ? parseInt(Big(sku.sell_value).times(100), 10)
          : form.sell_value;
        const sellableSku = {
          uuid: sku.uuid,
          code: sku.code,
          name: sku.name,
          stock_count: sku.stock_count,
          sell_value: sku.sell_value,
          deleted: sku.deleted
        };

        const savedBarcodes = [];
        const failedBarcodes = [];
        for (const barcode of sku.barcodes.slice()) {
          const { uuid, code } = barcode;
          let updatedBarcode = { nonEdited: true };

          if (barcode.mode === "edit") {
            updatedBarcode = await BarcodeRepository.update(
              sku.company_uuid,
              uuid,
              { code }
            );
          } else if (barcode.mode === "delete") {
            updatedBarcode = await BarcodeRepository.delete(
              sku.company_uuid,
              uuid
            );
          } else if (barcode.mode === "create") {
            updatedBarcode = await BarcodeRepository.create({
              code: barcode.code,
              type: barcode.type,
              company_uuid: sku.company_uuid,
              account_uuid: sku.company_uuid,
              sellable_uuid: sku.uuid,
              uuid: barcode.uuid
            });
          }

          if (!updatedBarcode.nonEdited) {
            updatedBarcode.success
              ? savedBarcodes.push(barcode.code)
              : failedBarcodes.push(barcode.code);
          }
        }

        const result = await ProductRepository.update(
          sellableSku.uuid,
          sellableSku
        );
        if (result.success) {
          sku.saved = true;
        }
        const prodMessage = result.success ? "atualizado!" : "falhou.";
        let finalProductMessage = `SKU '${sku.name}' ${prodMessage}`;

        if (savedBarcodes.slice().length) {
          finalProductMessage =
            finalProductMessage +
            `Códigos de barra atualizados: ${savedBarcodes.join(" - ")}`;
        }

        if (failedBarcodes.slice().length) {
          finalProductMessage =
            finalProductMessage +
            `Oops, os seguintes códigos de barra não foram atualizados: ${failedBarcodes.join(
              " - "
            )}`;
        }

        finalMessage.push(finalProductMessage);
      }
    }

    this.viewModel.skuLoading = false;
    return finalMessage;
  }

  verifyCrt() {
    if (!this.viewModel.companyFiscalInformations.crt_code) {
      this.sendNotification({
        type: "confirmation",
        title: "Deseja continuar?",
        content: `O CRT - Código de Regime Tributário da sua empresa não foi informado, \nfavor informá-lo para prosseguir com o cadastro do produto. Deseja continuar?`,
        actions: [
          {
            label: "Não",
            callback: () => this.onDefaultConfigurationChange(true)
          },
          {
            label: "Sim",
            type: "accent",
            callback: () => this.navigateTo("app.accountant")
          }
        ]
      });
    }
  }

  editBarcode(barcodeUuid, value) {
    let barcodes = this.viewModel.form.barcodes.slice();
    const index = _.findIndex(barcodes, ["uuid", barcodeUuid]);
    barcodes[index].mode =
      barcodes[index].mode !== "create" || !barcodes[index].mode
        ? "edit"
        : "create";
    barcodes[index].code = value;
  }

  deleteIndexError(index) {
    this.viewModel.errors.barcodeErrors.splice(index, 1);
  }
}



// WEBPACK FOOTER //
// ./src/components/app/registration/product/form/create.presenter.js